import * as React from 'react';
import { NotificationSettingsMap } from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { InjectedTranslateProps, translate } from 'react-i18next';
import {
  ChannelSettings,
  NotificationKeys,
  SettingsLayout,
  Loading,
  Block,
  BlockAlign,
} from '@wix/social-groups-common';
import {
  withNotificationSettings,
  WithNotificationsSettings,
} from '../Context/withNotificationSettings';
import { GroupNotificationSettings } from '../Context/NotificationSettings';
import styles from './NotificationSettings.st.css';
import { withAppData, WithAppDataProps } from '../Context';

interface NotificationSettingsComponentProps {
  onCancel();
}

interface NotificationSettingsProps
  extends InjectedTranslateProps,
    WithAppDataProps,
    WithNotificationsSettings,
    NotificationSettingsComponentProps {}

interface NotificationSettingsState {
  activeTabIndex: number;
  notificationSettings: GroupNotificationSettings;
}

const { WEB, MOBILE, EMAIL } = ApiTypes.v1.NotificationChannel;
const TABS = [EMAIL, MOBILE, WEB];

class NotificationSettingsComponent extends React.Component<
  NotificationSettingsProps,
  NotificationSettingsState
> {
  constructor(props: Readonly<NotificationSettingsProps>) {
    super(props);

    this.state = {
      activeTabIndex: 0,
      notificationSettings: props.notificationSettings,
    };
  }

  componentDidMount(): void {
    try {
      this.props.notificationActions.getNotificationSettings();
    } catch (e) {
      console.error('Get notification settings: FAIL');
    }
  }

  componentDidUpdate(prevProps: Readonly<NotificationSettingsProps>): void {
    if (!prevProps.notificationSettings && this.props.notificationSettings) {
      this.setState({
        notificationSettings: this.props.notificationSettings,
      });
    }
  }

  render() {
    const { t, isMobile } = this.props;
    const { activeTabIndex } = this.state;
    const title = t('groups-web.notifications');
    const cancel = t('groups-web.cancel');
    const save = t('groups-web.save');

    return (
      <SettingsLayout
        title={title}
        tabs={this.getItems()}
        activeTabIndex={activeTabIndex}
        cancelLabel={cancel}
        saveLabel={save}
        onTabClick={this.onTabClick}
        onCancel={this.onClose}
        onOk={this.save}
        mobile={isMobile}
        {...styles('root', {}, this.props)}
      >
        {this.renderActiveTab()}
      </SettingsLayout>
    );
  }

  onClose = () => {
    this.props.onCancel();
  };

  private readonly getItems = () => {
    const { t } = this.props;
    return [
      {
        title: t(NotificationKeys[EMAIL]),
      },

      {
        title: t(NotificationKeys[MOBILE]),
      },
      {
        title: t(NotificationKeys[WEB]),
      },
    ];
  };

  private readonly onTabClick = selectedTabIndex => {
    this.setState({
      activeTabIndex: selectedTabIndex,
    });
  };

  private renderActiveTab() {
    const { t, isMobile } = this.props;
    const { activeTabIndex, notificationSettings } = this.state;
    const channel = TABS[activeTabIndex];
    return notificationSettings ? (
      <ChannelSettings
        channel={channel}
        settings={notificationSettings[channel]}
        mute={this.mute}
        mobile={isMobile}
      />
    ) : (
      <Block autoContent align={BlockAlign.start} justify={BlockAlign.center}>
        <Loading />
      </Block>
    );
  }

  private readonly mute = (
    channel: ApiTypes.v1.NotificationChannel,
    type: ApiTypes.v1.NotificationType,
  ) => {
    const { notificationSettings } = this.state;
    const channelSettings = notificationSettings[channel];
    if (type === ApiTypes.v1.NotificationType.ALL) {
      this.muteAll(channelSettings);
    } else {
      this.muteType(channelSettings, type);
    }
    this.setState({ notificationSettings });
  };

  private muteType(
    channelSettings: NotificationSettingsMap,
    type: ApiTypes.v1.NotificationType,
  ) {
    const allMuted = channelSettings[ApiTypes.v1.NotificationType.ALL];

    const typeMuted = channelSettings[type];
    if (allMuted && typeMuted) {
      channelSettings[ApiTypes.v1.NotificationType.ALL] = !allMuted;
    }
    channelSettings[type] = !typeMuted;
  }

  private muteAll(channelSettings: NotificationSettingsMap) {
    const mute = !channelSettings[ApiTypes.v1.NotificationType.ALL];
    for (const key of Object.keys(channelSettings)) {
      channelSettings[key] = mute;
    }
  }
  private readonly save = () => {
    try {
      const { notificationSettings } = this.state;
      this.props.notificationActions.setNotificationSettings(
        notificationSettings,
      );
    } catch (e) {
      console.error('Save Notification settings: FAIL');
    }
    this.onClose();
  };
}

export const NotificationSettings = withNotificationSettings(
  withAppData(translate()(NotificationSettingsComponent)),
) as React.ComponentType<NotificationSettingsComponentProps>;
